import React, { Fragment, startTransition, useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col } from 'reactstrap';
import { isEqual } from 'lodash-es';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
import { VisitorModel, VisitorEntities } from 'client/data/models/visitor';
// HOCs
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
// Utils
import {
  getAllFacetsBySelectedValues,
  getFacetsBySelectedValues,
  getFilter,
  isUrlPatternEnabled,
} from 'site-modules/shared/utils/inventory/search-filter';
import { scrollToHash } from 'client/utils/scroll';
import { isClearAllFilters } from 'site-modules/shared/utils/inventory/srp-type-checkers';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import {
  getCurrentPaymentType,
  getSelectedPaymentType,
} from 'site-modules/shared/utils/inventory-utils/get-current-payment-type';
import { generateSEOBreadcrumbs, buildSearchFilterSEOObject } from 'site-modules/shared/utils/inventory/srp-seo-utils';
import { getFreshHistoryParam } from 'site-modules/shared/components/search-by-module/search-by-module-utils';
import { changeSrpRoute } from 'client/site-modules/car-buying-online/utils/routes';
import { getSortedInventoriesWithDeliveryFees } from 'client/site-modules/car-buying-online/utils/get-sorted-inventories';
import { isAvailableLocation } from 'site-modules/shared/utils/financing/financing';
import { getQuery } from 'client/utils/location';
import { handleReject } from 'client/site-modules/car-buying-online/utils/handle-reject';
// Constants
import {
  MAKE,
  MODEL,
  PAGE_NUMBER,
  SORT_BY,
  ELECTRICITY_RANGE,
  CREDIT_PROVIDER,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import {
  SKIP_TO_LINKS_CONFIG,
  SPR_SCROLL_TO_TOP_ANCHOR,
} from 'site-modules/shared/constants/inventory/routes-constants';
import { ONLINE_BTN } from 'site-modules/shared/constants/inventory/buying-experience';
import { PRICE_ASC, PRICE_DESC } from 'site-modules/shared/constants/sort-types';
import { CREDIT_PROVIDER_FACET } from 'site-modules/shared/constants/inventory/static-facets';
import { GLOBAL_HEADER_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { LINK_INFO_MAPPING } from 'site-modules/shared/components/inventory/constants/inventories-not-found-with-links';
import { BUY_ONLINE_SRP_PATHNAME } from 'site-modules/shared/utils/srp-link-constructor';
import { PREQUAL_MSG } from 'site-modules/shared/constants/allowed-seo-srp-request-params';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
// Shared components
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { CertifiedPreOwned } from 'site-modules/shared/components/certified-pre-owned/certified-pre-owned';
import { InventoryAdTargetingPrefetch } from 'site-modules/shared/components/native-ad/inventory-ad-targeting/inventory-ad-targeting-prefetch';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { EvEBuyingExperienceButtonGroup } from 'site-modules/shared/components/eve-buying-experience-button-group/eve-buying-experience-button-group';
import { UsurpFiltersPanel } from 'site-modules/shared/components/inventory/usurp-filters-panel/usurp-filters-panel';
import { UsurpStickyHeader } from 'site-modules/shared/components/inventory/usurp-sticky-header/usurp-sticky-header';
import { UsurpFiltersDrawer } from 'site-modules/shared/components/inventory/usurp-filters-drawer/usurp-filters-drawer';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { SrpCardsTopPanel } from 'site-modules/shared/components/inventory/srp-cards-top-panel/srp-cards-top-panel';
import { SrpPagination } from 'client/site-modules/shared/components/inventory/srp-pagination/srp-pagination';
import { InventoriesNotFoundWithLinks } from 'site-modules/shared/components/inventory/inventories-not-found-with-links/inventories-not-found-with-links';
import { UsurpCardList } from 'site-modules/shared/components/inventory/usurp-card-list/usurp-card-list';
// Components
import { GreatDealsLinkWidgetTable } from 'client/site-modules/car-buying-online/components/great-deals-link-widget-table/great-deals-link-widget-table';
import { LeaseDealsLinkWidgetTable } from 'client/site-modules/car-buying-online/components/lease-deals-link-widget-table/lease-deals-link-widget-table';
import { SrpDisclaimer } from 'client/site-modules/car-buying-online/components/srp-disclaimer/srp-disclaimer';
import { UsurpFilters } from 'client/site-modules/car-buying-online/components/usurp-filters/usurp-filters';
import { SrpTopMessage } from 'site-modules/car-buying-online/components/srp-top-message/srp-top-message';
import { SuggestedFacets } from 'site-modules/car-buying-online/components/suggested-facets/suggested-facets';
import { SrpStickyHeaderTitle } from 'site-modules/car-buying-online/components/usurp-sticky-header-title/usurp-sticky-header-title';
import { UsurpInventoryCard } from 'client/site-modules/car-buying-online/components/usurp-inventory-card/usurp-inventory-card';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './car-buying-online.scss';

const isMultiMMInitial = selectedFacets =>
  selectedFacets && (selectedFacets[MAKE]?.length > 1 || selectedFacets[MODEL]?.length > 1);

const scrollToTop = () => {
  scrollToHash(`#${SPR_SCROLL_TO_TOP_ANCHOR}`, false);
};

const changeRoute = (updatedFiltersData, { urlPathname } = {}) => {
  setTimeout(() => {
    changeSrpRoute(updatedFiltersData, { urlPathname });
  }, 1);
};

export const CarBuyingOnlineUI = ({
  zipCode,
  selectedFacets = {},
  inventoryData: inventoryDataProp = null,
  isMobile,
  urlContext = {},
  visitorLocation = {},
  location = {},
  disableInventoryMLPhotoSort = false,
  includeDeliveryFee = false,
  isPrequalified = false,
  setModelValue,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const prevIncludeDeliveryFee = usePrevious(includeDeliveryFee);
  const pathName = location.pathname;
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isCancelInProgress, setIsCancelInProgress] = useState(false);
  const [prevSelectedFilters, setPrevSelectedFilters] = useState(null);
  const [isMultiMM, setIsMultiMM] = useState(isMultiMMInitial(selectedFacets));
  const [inventoryData, setInventoryData] = useState(inventoryDataProp);

  /**
   * Sets updated facet value to a model
   * @param {string} type facet type
   * @param {Object} facet modified value
   * @param {boolean} isChecked whether value is selected (to exclude value if already selected)
   */
  const updateFilter = useCallback(
    (type, facet, isChecked) => {
      setIsLoading(true);
      const urlPattern = urlContext?.urlPattern;
      const initialUrlPattern = selectedFacets.initialUrlPattern;
      const shouldPersistUrlPattern = isUrlPatternEnabled({ initialUrlPattern, urlContext });
      const isClearAllFiltersAgain = !(type && facet) && isClearAllFilters(selectedFacets);
      const searchResultsData = getFilter({
        type,
        facet,
        isChecked,
        currentFilter: {
          ...selectedFacets,
          ...(shouldPersistUrlPattern && { initialUrlPattern: urlPattern || initialUrlPattern }),
        },
        isMultiMM,
        facets: inventoryData?.facets || [],
      });

      if (!isClearAllFiltersAgain) {
        changeRoute(searchResultsData, { urlPathname: pathName });
      }
    },
    [inventoryData, isMultiMM, pathName, selectedFacets, urlContext]
  );

  const onSuggestedFacetUpdate = useCallback(
    (...args) => {
      scrollToTop();
      updateFilter(...args);
    },
    [updateFilter]
  );

  const onRemoveCapOnePrequalParam = useCallback(
    () => () => {
      const query = getQuery(location);
      delete query[PAGE_NUMBER];

      changeRoute(query);
    },
    [location]
  );

  const updateZipCode = useCallback(
    newZipCode => {
      setIsLoading(true);
      return setModelValue('location', VisitorModel, { zipCode: newZipCode })
        .then(() => {
          const baseParams = { ...selectedFacets, ...getFreshHistoryParam() };
          delete baseParams[PAGE_NUMBER];

          changeRoute({ ...baseParams }, { urlPathname: pathName });
        })
        .catch(e => handleReject(e))
        .finally(() => {
          setIsLoading(false);
        });
    },
    [pathName, selectedFacets, setModelValue]
  );

  const toggleDeliveryFees = useCallback(
    include => {
      setIsLoading(true);
      const baseParams = { ...selectedFacets, deliveryFee: include };
      delete baseParams[PAGE_NUMBER];

      changeRoute({ ...baseParams }, { urlPathname: pathName });
    },
    [pathName, selectedFacets]
  );

  const openFiltersDrawer = useCallback(() => {
    setPrevSelectedFilters(selectedFacets);
    startTransition(() => {
      setIsFilterDrawerOpen(true);
    });
  }, [selectedFacets]);

  const closeFiltersDrawer = useCallback(() => {
    startTransition(() => {
      setIsFilterDrawerOpen(false);
    });
  }, []);

  const cancelFiltersUpdate = useCallback(() => {
    setIsCancelInProgress(true);
    if (!isEqual(prevSelectedFilters, selectedFacets)) {
      setIsLoading(true);
      changeRoute(prevSelectedFilters, { urlPathname: pathName });
    }
  }, [pathName, prevSelectedFilters, selectedFacets]);

  const appendStaticFacets = facets => {
    const visitorStateCode = visitorLocation.stateCode;
    return isPrequalified && isAvailableLocation(visitorStateCode) ? [...facets, CREDIT_PROVIDER_FACET] : facets;
  };

  if (prevIncludeDeliveryFee !== includeDeliveryFee) {
    if (isLoading) {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (inventoryDataProp) {
      if (isLoading && !isEqual(inventoryDataProp, inventoryData)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsLoading(false);
      }
      setInventoryData(inventoryDataProp);

      if (isCancelInProgress && isEqual(inventoryDataProp, inventoryData)) {
        startTransition(() => {
          closeFiltersDrawer();
        });
        setIsCancelInProgress(false);
      }
    }
  }, [closeFiltersDrawer, inventoryData, inventoryDataProp, isCancelInProgress, isLoading]);

  // Inventory data
  const inventory = inventoryData?.inventories?.results || [];
  const {
    totalPages,
    pageSize,
    pageNumber,
    totalNumber: inventoryCount,
    maxAllowedPageNumber,
  } = inventoryData?.inventories || {};
  const attributes = inventoryData?.attributes || {};
  const suggestedFacets = inventoryData?.suggestedFacets || [];
  const make = attributes?.vehicle?.make?.name || '';
  const inventoryType = attributes?.inventoryType || '';

  // SEO
  const seoData = useMemo(
    () =>
      buildSearchFilterSEOObject({
        searchResultsFilter: selectedFacets,
        urlContext,
        inventories: inventory[0],
        attributes,
        inventoryCount,
        pageNumber,
      }),
    [attributes, inventory, inventoryCount, pageNumber, selectedFacets, urlContext]
  );

  // Facets
  const facets = appendStaticFacets([...(inventoryData?.facets || [])]);
  const paymentType = getCurrentPaymentType(selectedFacets, attributes?.paymentType);
  const selectedPaymentType = getSelectedPaymentType(selectedFacets);
  const allSelectedFacets = getAllFacetsBySelectedValues(facets, selectedFacets);
  const filteringByFacets = getFacetsBySelectedValues(allSelectedFacets);

  const breadcrumbsData = useMemo(
    () =>
      generateSEOBreadcrumbs({
        originalUrl: pathName,
        seoData,
        includeLastLink: true,
      }),
    [pathName, seoData]
  );

  // Link widgets
  const greatDealsWidgetData = inventoryData?.seoInfo?.greatDealsWidget;
  const leaseDealsWidgetData = inventoryData?.seoInfo?.leaseDealsWidget;

  // Others
  const displayLowInvModule = inventoryCount <= 5 && !isLoading && !isCancelInProgress;
  const query = getQuery(location);
  const userCapOnePrequalApproved = !!query[CREDIT_PROVIDER] && !!query[PREQUAL_MSG] && isPrequalified;

  const facetsProps = {
    paymentType: selectedPaymentType,
    facets,
    filteringByFacets,
    onZipCodeUpdate: updateZipCode,
    onUpdate: updateFilter,
    selectedFacets,
    setIsMultiMM,
    searchResultsFound: inventoryCount,
    includeDeliveryFee,
    isMobile,
    onToggleDeliveryFees: toggleDeliveryFees,
  };

  const currentSort = selectedFacets?.[`${SORT_BY}[0]`] || '';
  const isPricingSort = [PRICE_ASC, PRICE_DESC].includes(currentSort);
  let sortedInventory = inventory;
  if (includeDeliveryFee && isPricingSort) {
    const sortedOrder = currentSort === PRICE_ASC ? 'asc' : 'desc';
    sortedInventory = getSortedInventoriesWithDeliveryFees(inventory, sortedOrder);
  }

  const srpCardsTopPanel = (
    <SrpCardsTopPanel
      onUpdate={updateFilter}
      inventoryCount={inventoryCount}
      withRange={!!facets.find(({ type }) => type === ELECTRICITY_RANGE)}
      selectedFacets={selectedFacets}
      isMobile={isMobile}
    />
  );

  return (
    <main id="main-content" tabIndex={-1} className="usurp">
      <LoadingSpinner isLoading={isLoading && !isFilterDrawerOpen && !isCancelInProgress} classes="bg-cool-gray-90">
        <UsurpStickyHeader
          seoData={seoData}
          facets={facets}
          selectedFacets={selectedFacets}
          suggestedFacets={suggestedFacets}
          onFiltersBtnClick={openFiltersDrawer}
          onUpdate={isMobile ? onSuggestedFacetUpdate : updateFilter}
          isMobile={isMobile}
          titleComponent={SrpStickyHeaderTitle}
          suggestedFacetsComponent={SuggestedFacets}
          filteringByFacets={filteringByFacets}
        />
        <Container className="pb-3">
          <Row className="flex-row-reverse">
            <Col
              xs={12}
              lg={9}
              role="region"
              aria-label="Listings"
              tabIndex={-1}
              id={SKIP_TO_LINKS_CONFIG.RESULTS_CONTAINER_ID}
            >
              {!isMobile && (
                <ScrollLink
                  to={SKIP_TO_LINKS_CONFIG.FILTERS_CONTAINER_ID}
                  className="visually-hidden-focusable d-none d-lg-block"
                  scrollConfig={{ offset: GLOBAL_HEADER_OFFSET }}
                >
                  {SKIP_TO_LINKS_CONFIG.FILTERS_CONTAINER_LABEL}
                </ScrollLink>
              )}

              {isMobile && (
                <EvEBuyingExperienceButtonGroup
                  selectedFacets={selectedFacets}
                  isMobile={isMobile}
                  selectedBtn={ONLINE_BTN}
                  showDeliveryFreeToggle
                  toggleDeliveryFees={toggleDeliveryFees}
                  includeDeliveryFee={includeDeliveryFee}
                />
              )}

              {!isMobile && srpCardsTopPanel}

              <SrpTopMessage
                inventoryData={inventoryData}
                pathName={pathName}
                isMobile={isMobile}
                userCapOnePrequalApproved={userCapOnePrequalApproved}
                onUpdate={updateFilter}
                onRemoveCapOnePrequalParam={onRemoveCapOnePrequalParam}
              />

              {isMobile && srpCardsTopPanel}

              <UsurpCardList
                inventory={sortedInventory}
                visitorLocation={visitorLocation}
                paymentType={paymentType}
                selectedPaymentType={selectedPaymentType}
                isMobile={isMobile}
                disableInventoryMLPhotoSort={disableInventoryMLPhotoSort}
                zipCode={zipCode}
                includeDeliveryFee={includeDeliveryFee}
                cardComponent={UsurpInventoryCard}
                cardKeyPrefix={inventoryData?.hash}
              />

              {displayLowInvModule && (
                <InventoriesNotFoundWithLinks
                  attributes={attributes}
                  selectedFacets={selectedFacets}
                  inventoriesCount={inventoryCount}
                  isFilterDrawerOpen={isFilterDrawerOpen}
                  onUpdate={updateFilter}
                  mapping={LINK_INFO_MAPPING}
                  basePathname={BUY_ONLINE_SRP_PATHNAME}
                />
              )}
              <SrpPagination
                pageNumber={pageNumber}
                pagesCount={totalPages}
                maxAllowedPageNumber={maxAllowedPageNumber}
                pageSize={pageSize}
                pageResultsCount={inventory.length}
                inventoryCount={inventoryCount}
                selectedFacets={selectedFacets}
                urlContext={urlContext}
                pathName={pathName}
                isMobile={isMobile}
              />
              <Breadcrumbs data={breadcrumbsData} className="p-0 my-1" />
              {isMobile && (
                <Fragment>
                  <div className="mt-1 d-flex">
                    {!isLoading && (
                      <InventoryAdTargetingPrefetch>
                        <MrectNative wrapperClass="mrect mx-auto" mobileOnly refreshable={false} useMinHeight />
                      </InventoryAdTargetingPrefetch>
                    )}
                  </div>
                  <div className="my-1">
                    {selectedFacets.make && selectedFacets.model && !isLoading && (
                      <Experiment name="ads-12460_dfp" showDefault>
                        <Recipe name="ctrl" isDefault>
                          <InventoryAdTargetingPrefetch>
                            <NativeAd
                              wrapperClass="mx-auto filmstrip"
                              nativeStyle="filmstrip"
                              position={'1'}
                              refreshable
                              noRefresh={['scroll', 'engagement']}
                              mobileOnly
                              useMinHeight
                            />
                          </InventoryAdTargetingPrefetch>
                        </Recipe>
                        <Recipe name="chal" />
                      </Experiment>
                    )}
                  </div>
                </Fragment>
              )}
              {Boolean(isCpo(inventoryType) && make) && (
                <CertifiedPreOwned make={make} showMainHeader className="mt-2" />
              )}
              {Boolean(greatDealsWidgetData) && (
                <GreatDealsLinkWidgetTable data={greatDealsWidgetData} isMobile={isMobile} className="mt-2" />
              )}
              {Boolean(leaseDealsWidgetData) && (
                <LeaseDealsLinkWidgetTable
                  make={make}
                  data={leaseDealsWidgetData}
                  isMobile={isMobile}
                  className="mt-2"
                />
              )}
            </Col>
            {!isMobile && (
              <Col lg={3} className="d-none d-lg-block filters-column">
                <ErrorBoundary>
                  <UsurpFiltersPanel>
                    <UsurpFilters {...facetsProps} />
                  </UsurpFiltersPanel>
                </ErrorBoundary>
                <div className="first-mrect-wrapper hidden-xl-down">
                  {!isLoading && (
                    <InventoryAdTargetingPrefetch>
                      <NativeAd
                        wrapperClass="mrect mx-auto"
                        adNameWired="mrect"
                        adNameMobile="mmrect"
                        nativeStyle="mediumrectangle"
                        showLabel
                        refreshable
                        noRefresh={['scroll', 'engagement']}
                        wiredBreakpoints={{
                          xl: true,
                        }}
                        position={'1'}
                      />
                    </InventoryAdTargetingPrefetch>
                  )}
                </div>
                {selectedFacets.make && selectedFacets.model && !isLoading && (
                  <Experiment name="ads-12460_dfp" showDefault>
                    <Recipe name="ctrl" isDefault>
                      <InventoryAdTargetingPrefetch>
                        <NativeAd
                          wrapperClass="my-2_5 filmstrip"
                          nativeStyle="filmstrip"
                          position={'1'}
                          refreshable
                          noRefresh={['scroll', 'engagement']}
                          wiredOnly
                        />
                      </InventoryAdTargetingPrefetch>
                    </Recipe>
                    <Recipe name="chal" />
                  </Experiment>
                )}
                <div className="second-mrect-wrapper hidden-xl-down">
                  {!isLoading && (
                    <InventoryAdTargetingPrefetch>
                      <MrectNative
                        position={'2'}
                        refreshable={false}
                        wiredBreakpoints={{
                          xl: true,
                        }}
                        addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                      />
                    </InventoryAdTargetingPrefetch>
                  )}
                </div>
              </Col>
            )}
            <UsurpFiltersDrawer
              isOpen={isFilterDrawerOpen}
              onToggle={closeFiltersDrawer}
              onCancel={cancelFiltersUpdate}
              onApply={closeFiltersDrawer}
              searchResultsFound={inventoryCount}
              isLoading={isLoading}
              dataTrackingParent="srp-new-drawer-facets"
              isMobile={isMobile}
              filtersComponent={UsurpFilters}
              {...facetsProps}
            />
          </Row>
          <SrpDisclaimer inventory={inventory} />
        </Container>
      </LoadingSpinner>
    </main>
  );
};

CarBuyingOnlineUI.propTypes = {
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }),
  radius: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  setModelValue: PropTypes.func.isRequired,
  selectedFacets: PropTypes.shape({}),
  urlContext: PropTypes.shape({}),
  zipCode: PropTypes.string.isRequired,
  location: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }),
  disableInventoryMLPhotoSort: PropTypes.bool,
  includeDeliveryFee: PropTypes.bool,
  isPrequalified: PropTypes.bool,
};

export const CarBuyingOnline = withInventoryListTracking(memo(CarBuyingOnlineUI));
